import { computed, onBeforeUnmount, readonly, ref, type Ref } from 'vue'

export type AppVersionChannel = 'prod' | 'beta' | 'nightly'
export type AppVersionSeverity = 'none' | 'gentle' | 'error' | 'unsupported'
export type AppVersionUpdateKind = Exclude<AppVersionSeverity, 'none'>

export interface AppVersionInfo {
  app: string
  channel: AppVersionChannel
  version: string
  releasedAt: string
  minimumSupported: Record<AppVersionUpdateKind, string>
}

export interface AppVersionDecision {
  severity: AppVersionSeverity
  loaded: Readonly<AppVersionInfo>
  latest?: AppVersionInfo
  reason: 'current' | 'newer-than-live' | 'newer-version' | 'week-old' | 'gentle-floor' | 'error-floor' | 'unsupported-floor' | 'unavailable'
}

export interface AppVersionMonitorOptions {
  loadedVersion: AppVersionInfo
  versionUrl?: string
  fetcher?: typeof fetch
  pollIntervalMs?: number
  staleAfterMs?: number
  now?: () => Date
}

export interface AppVersionMonitor {
  loadedVersion: Readonly<AppVersionInfo>
  latestVersion: Readonly<Ref<AppVersionInfo | null>>
  decision: Readonly<Ref<AppVersionDecision>>
  isChecking: Readonly<Ref<boolean>>
  error: Readonly<Ref<unknown>>
  checkNow: () => Promise<void>
  stop: () => void
}

const DAY_MS = 24 * 60 * 60 * 1000
export const DEFAULT_APP_VERSION_URL = '/app-version.json'
export const DEFAULT_APP_VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000
export const DEFAULT_APP_VERSION_STALE_AFTER_MS = 7 * DAY_MS

export function compareAppVersions(a: string, b: string): number {
  const left = parseAppVersion(a)
  const right = parseAppVersion(b)

  for (let index = 0; index < left.length; index += 1) {
    const delta = (left[index] ?? 0) - (right[index] ?? 0)
    if (delta !== 0) {
      return delta > 0 ? 1 : -1
    }
  }

  return 0
}

export function isAppVersionOlderThan(a: string, b: string): boolean {
  return compareAppVersions(a, b) < 0
}

export function resolveAppVersionDecision(
  loaded: AppVersionInfo,
  latest: AppVersionInfo | null,
  options: Pick<AppVersionMonitorOptions, 'now' | 'staleAfterMs'> = {},
): AppVersionDecision {
  if (!latest) {
    return { severity: 'none', loaded, reason: 'unavailable' }
  }

  if (loaded.app !== latest.app || loaded.channel !== latest.channel) {
    return { severity: 'none', loaded, latest, reason: 'current' }
  }

  if (!isAppVersionOlderThan(loaded.version, latest.version)) {
    return {
      severity: 'none',
      loaded,
      latest,
      reason: compareAppVersions(loaded.version, latest.version) > 0 ? 'newer-than-live' : 'current',
    }
  }

  if (isAppVersionOlderThan(loaded.version, latest.minimumSupported.unsupported)) {
    return { severity: 'unsupported', loaded, latest, reason: 'unsupported-floor' }
  }

  if (isAppVersionOlderThan(loaded.version, latest.minimumSupported.error)) {
    return { severity: 'error', loaded, latest, reason: 'error-floor' }
  }

  if (isAppVersionOlderThan(loaded.version, latest.minimumSupported.gentle)) {
    return { severity: 'gentle', loaded, latest, reason: 'gentle-floor' }
  }

  const releasedAt = Date.parse(loaded.releasedAt)
  const staleAfterMs = options.staleAfterMs ?? DEFAULT_APP_VERSION_STALE_AFTER_MS
  const now = options.now?.() ?? new Date()

  if (Number.isFinite(releasedAt) && now.getTime() - releasedAt >= staleAfterMs) {
    return { severity: 'gentle', loaded, latest, reason: 'week-old' }
  }

  return { severity: 'gentle', loaded, latest, reason: 'newer-version' }
}

export function createAppVersionMonitor(options: AppVersionMonitorOptions): AppVersionMonitor {
  const loadedVersion = Object.freeze({ ...options.loadedVersion, minimumSupported: { ...options.loadedVersion.minimumSupported } })
  const latestVersion = ref<AppVersionInfo | null>(null)
  const isChecking = ref(false)
  const error = ref<unknown>(null)
  const pollIntervalMs = options.pollIntervalMs ?? DEFAULT_APP_VERSION_POLL_INTERVAL_MS
  const versionUrl = options.versionUrl ?? DEFAULT_APP_VERSION_URL
  const fetcher = options.fetcher ?? (typeof globalThis.fetch === 'function' ? globalThis.fetch.bind(globalThis) : undefined)

  let timer: ReturnType<typeof setInterval> | undefined

  const decision = computed(() =>
    resolveAppVersionDecision(loadedVersion, latestVersion.value, {
      now: options.now,
      staleAfterMs: options.staleAfterMs,
    }),
  )

  async function checkNow() {
    if (!fetcher || isChecking.value) {
      return
    }

    isChecking.value = true
    try {
      const response = await fetcher(withCacheBust(versionUrl), {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      })

      if (!response.ok) {
        throw new Error(`Version check failed with HTTP ${response.status}`)
      }

      latestVersion.value = normalizeVersionInfo(await response.json())
      error.value = null
    } catch (caught) {
      error.value = caught
    } finally {
      isChecking.value = false
    }
  }

  function stop() {
    if (timer) {
      clearInterval(timer)
      timer = undefined
    }
  }

  if (pollIntervalMs > 0 && fetcher) {
    timer = setInterval(() => {
      void checkNow()
    }, pollIntervalMs)
  }

  void checkNow()

  return {
    loadedVersion,
    latestVersion: readonly(latestVersion),
    decision: readonly(decision),
    isChecking: readonly(isChecking),
    error: readonly(error),
    checkNow,
    stop,
  }
}

export function useAppVersionMonitor(options: AppVersionMonitorOptions): AppVersionMonitor {
  const monitor = createAppVersionMonitor(options)
  onBeforeUnmount(monitor.stop)
  return monitor
}

function parseAppVersion(version: string): [number, number, number] {
  const match = /^(\d+)\.(\d{4})\.(\d+)$/.exec(version)
  if (!match) {
    throw new Error(`Invalid app version "${version}". Expected X.YYWW.V.`)
  }

  return [Number(match[1]), Number(match[2]), Number(match[3])]
}

function normalizeVersionInfo(value: unknown): AppVersionInfo {
  const candidate = value as Partial<AppVersionInfo>

  if (
    !candidate
    || typeof candidate.app !== 'string'
    || !isChannel(candidate.channel)
    || typeof candidate.version !== 'string'
    || typeof candidate.releasedAt !== 'string'
    || !candidate.minimumSupported
    || typeof candidate.minimumSupported.gentle !== 'string'
    || typeof candidate.minimumSupported.error !== 'string'
    || typeof candidate.minimumSupported.unsupported !== 'string'
  ) {
    throw new Error('Invalid app version payload.')
  }

  return {
    app: candidate.app,
    channel: candidate.channel,
    version: candidate.version,
    releasedAt: candidate.releasedAt,
    minimumSupported: {
      gentle: candidate.minimumSupported.gentle,
      error: candidate.minimumSupported.error,
      unsupported: candidate.minimumSupported.unsupported,
    },
  }
}

function isChannel(value: unknown): value is AppVersionChannel {
  return value === 'prod' || value === 'beta' || value === 'nightly'
}

function withCacheBust(url: string) {
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}t=${Date.now()}`
}
